import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AuctioneerDataMatrixTable from './AuctioneerDataMatrixTable';
import { auctioneerDataMatrixItemsType, auctioneerDataTemplatesType, countriesType, reducedMembersType } from '../../types';
import SelectField from '../fields/SelectField';
import ResetFieldButton from '../buttons/ResetFieldButton';
import './_styles_/AuctioneerDataMatrix.css';
import NumberField from '../fields/NumberField';
import useSingleValidator from '../../hooks/useSingleValidator';
import { auctioneerDataMatrixQuantityValidator } from '../../validators/auctioneerDataMatrix';
import useExtendedState from '../../hooks/useExtendedState';
import Constants from '../../utils/Constants';
import { hasPrivilege } from '../../hocs/withPrivileges';
import useAuthentication from '../../hooks/useAuthentication';
import { auctionAuctioneerDataTemplateItemsValidator } from '../../validators/auctions';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import useEffectOnLoad from '../../hooks/useEffectOnLoad';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

AuctioneerDataMatrix.propTypes = {
    availableAuctioneers: reducedMembersType.isRequired,
    availableCountries: countriesType.isRequired,
    maxQuantitySum: PropTypes.number.isRequired,
    items: auctioneerDataMatrixItemsType.isRequired,
    validator: PropTypes.func.isRequired,
    onItemsChange: PropTypes.func.isRequired,
    onReset: PropTypes.func,
    isChanged: PropTypes.bool,
    isRequired: PropTypes.bool,
    sufficientPrivileges: PropTypes.arrayOf(PropTypes.string),
    availableTemplates: auctioneerDataTemplatesType,
    validateOnLoad: PropTypes.bool,
    doResetComponentsAfterModification: PropTypes.bool,
    doAdjustValuesAfterExternalUpdate: PropTypes.bool,
};

AuctioneerDataMatrix.defaultProps = {
    onReset: null,
    isChanged: false,
    isRequired: false,
    sufficientPrivileges: null,
    availableTemplates: null,
    validateOnLoad: false,
    doResetComponentsAfterModification: false,
    doAdjustValuesAfterExternalUpdate: false,
};

function AuctioneerDataMatrix(props) { // NOSONAR javascript:S3776
    const {
        availableAuctioneers,
        availableCountries,
        availableTemplates,
        maxQuantitySum,
        items,
        validator,
        onItemsChange,
        onReset,
        isChanged,
        isRequired,
        sufficientPrivileges,
        validateOnLoad,
        doResetComponentsAfterModification,
        doAdjustValuesAfterExternalUpdate,
    } = props;
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const isModifiable = sufficientPrivileges === null || hasPrivilege(userPrivileges, sufficientPrivileges);
    const memberIdToAddState = useExtendedState('');
    const countryCodeToAddState = useExtendedState('');
    const quantityToAddState = useExtendedState(null);
    const templateIdToApplyState = useExtendedState('');
    const [validationWarning, setValidationWarning] = useState(null);
    const [validationError, setValidationError] = useState(null);
    const [adjustValuesAfterTemplateApplied, setAdjustValuesAfterTemplateApplied] = useState(false);
    const {
        validator: quantityValidator,
        isValid: isQuantityValid,
        reset: resetQuantityValidator,
    } = useSingleValidator(false, useMemo(() => compositeValidator(auctioneerDataMatrixQuantityValidator(t, Constants.MIN_AUCTIONEER_DATA_QUANTITY, maxQuantitySum), wafValidator(t)), [t, maxQuantitySum]));
    const {
        validator: selectedTemplateValidator,
        reset: resetSelectedTemplateValidator,
    } = useSingleValidator(true, useMemo(() => auctionAuctioneerDataTemplateItemsValidator(t, maxQuantitySum), [t, maxQuantitySum]));
    const validateSelectedTemplate = (templateIdForValidation) => {
        if (templateIdForValidation) {
            const templateIdForValidationInt = parseInt(templateIdForValidation, 10);
            const templateForValidation = availableTemplates.find((availableTemplate) => availableTemplate.id === templateIdForValidationInt);
            selectedTemplateValidator(templateForValidation.templateItems)
                .then(() => setValidationWarning(null))
                .catch((e) => setValidationWarning(e));
        } else {
            resetSelectedTemplateValidator();
            setValidationWarning(null);
        }
    };
    const [doReset, setDoReset] = useState(false);
    const onResetInternal = () => {
        setDoReset(true);
        onReset();
    };
    const changeItems = (newItems) => {
        validator(newItems)
            .then(() => setValidationError(null))
            .catch((e) => setValidationError(e));
        onItemsChange(newItems);
        setAdjustValuesAfterTemplateApplied(false);
        setDoReset(false);
    };
    useEffectOnUpdate(() => {
        validator(items)
            .then(() => setValidationError(null))
            .catch((e) => setValidationError(e));
        validateSelectedTemplate(templateIdToApplyState.value);
        // "validator" is intentionally excluded from dependency array of useEffect() because it's recreated on render, so it would end up in endless loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, maxQuantitySum]);
    useEffectOnLoad(() => {
        if (validateOnLoad) {
            validator(items)
                .then(() => setValidationError(null))
                .catch((e) => setValidationError(e));
            validateSelectedTemplate(templateIdToApplyState.value);
        }
    });
    const addItem = () => {
        changeItems([...items, {
            auctioneer: availableAuctioneers.find((availableAuctioneer) => availableAuctioneer.memberId === memberIdToAddState.value),
            country: availableCountries.find((availableCountry) => availableCountry.code === countryCodeToAddState.value),
            quantity: quantityToAddState.value,
        }]);
        memberIdToAddState.reset();
        countryCodeToAddState.reset();
        quantityToAddState.reset();
        resetQuantityValidator();
    };
    const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(true);
    isQuantityValid()
        .then(() => setIsAddButtonDisabled(!memberIdToAddState.value || !countryCodeToAddState.value))
        .catch(() => setIsAddButtonDisabled(true));

    const isApplyTemplateButtonDisabled = !templateIdToApplyState.value;
    const removeItem = (itemToRemove) => () => {
        changeItems(_.without(items, itemToRemove));
    };
    const changeQuantity = (itemToChange) => (newQuantity) => {
        const newItems = items.map((item) => {
            let result = item;
            if (item === itemToChange) {
                result = { ...item, quantity: newQuantity };
            }
            return result;
        });
        changeItems(newItems);
    };
    const onNewTemplateSelected = (newTemplateIdToApply) => {
        validateSelectedTemplate(newTemplateIdToApply);
    };
    const applyTemplate = () => {
        const templateIdToApplyInt = parseInt(templateIdToApplyState.value, 10);
        const templateToApply = availableTemplates.find((availableTemplate) => availableTemplate.id === templateIdToApplyInt);
        changeItems([...templateToApply.templateItems]);
        memberIdToAddState.reset();
        countryCodeToAddState.reset();
        quantityToAddState.reset();
        templateIdToApplyState.reset();
        resetQuantityValidator();
        setAdjustValuesAfterTemplateApplied(true);
    };
    const groupedItems = _.groupBy(items, 'auctioneer.memberId');
    const groupedMemberIdsCountryCodes = _.mapValues(
        groupedItems,
        (arrOfTmpItems) => _.map(_.uniqBy(arrOfTmpItems, 'country.code'), 'country.code'),
    );
    const realAvailableAuctioneers = availableAuctioneers.filter((availableAuctioneer) => (
        !groupedMemberIdsCountryCodes[availableAuctioneer.memberId]
            || groupedMemberIdsCountryCodes[availableAuctioneer.memberId].length < availableCountries.length
    ));
    const countryCodesForSelectedMember = groupedMemberIdsCountryCodes[memberIdToAddState.value] || [];
    let realAvailableCountries = [];
    if (memberIdToAddState.value) {
        realAvailableCountries = countryCodesForSelectedMember.length === 0
            ? availableCountries
            : availableCountries.filter((availableCountry) => !countryCodesForSelectedMember.includes(availableCountry.code));
    }
    return (
        <div className={`auctioneer-data-matrix field${!isModifiable ? ' readonly' : ''}${isChanged ? ' modified' : ''}${(isRequired && !items.length) ? ' required' : ''}`}>
            <div className="auctioneer-data-matrix-field">
                <div className="field--key">
                    {t('auctioneer-data-matrix.title')}
                    <div className="auctioneer-data-matrix--validations">
                        {validationError && (
                            <div className="validation-error auctioneer-data-matrix--error">
                                {validationError}
                            </div>
                        )}
                        {validationWarning && (
                            <div className="validation-warning auctioneer-data-matrix--warning">
                                {validationWarning}
                            </div>
                        )}
                    </div>
                </div>
                {isModifiable && (
                    <div className="auctioneer-data-matrix--add-items">
                        <div className="auctioneer-data-matrix--add-item">
                            <SelectField
                                label={t('auctioneer-data-matrix.member')}
                                inputId="auctioneer-data-matrix--fields--member"
                                className="auctioneer-data-matrix--fields--member"
                                state={memberIdToAddState}
                                emptyOption={<option value="">...</option>}
                                options={realAvailableAuctioneers.map((availableMember) => (
                                    <option
                                        key={availableMember.memberId}
                                        value={availableMember.memberId}
                                    >
                                        {availableMember.name} ({availableMember.memberId})
                                    </option>
                                ))}
                            />
                            <SelectField
                                label={t('auctioneer-data-matrix.country')}
                                inputId="auctioneer-data-matrix--fields--country"
                                className="auctioneer-data-matrix--fields--country"
                                state={countryCodeToAddState}
                                emptyOption={<option value="">...</option>}
                                options={realAvailableCountries.map((availableCountry) => (
                                    <option
                                        key={availableCountry.code}
                                        value={availableCountry.code}
                                    >
                                        {availableCountry.name} ({availableCountry.code})
                                    </option>
                                ))}
                                disabled={!memberIdToAddState.value}
                            />
                            <NumberField
                                label={t('auctioneer-data-matrix.quantity')}
                                inputId="auctioneer-data-matrix--fields--quantity"
                                className="auctioneer-data-matrix--fields--quantity"
                                validator={quantityValidator}
                                state={quantityToAddState}
                                min={Constants.MIN_AUCTIONEER_DATA_QUANTITY}
                                max={maxQuantitySum}
                                disabled={!memberIdToAddState.value || !countryCodeToAddState.value}
                            />
                            <button
                                type="button"
                                className="add-item-button button button-bordered"
                                disabled={isAddButtonDisabled}
                                onClick={addItem}
                            >
                                <FontAwesomeIcon icon="user-plus" />&nbsp;{t('auctioneer-data-matrix.add-item')}
                            </button>
                        </div>
                        {availableTemplates && availableTemplates.length > 0 && (
                            <div className="auctioneer-data-matrix--apply-template">
                                <SelectField
                                    label={t('auctioneer-data-matrix.template')}
                                    inputId="auctioneer-data-matrix--fields--template"
                                    className="auctioneer-data-matrix--fields--template"
                                    onChange={onNewTemplateSelected}
                                    emptyOption={<option value="">...</option>}
                                    state={templateIdToApplyState}
                                    options={availableTemplates.map((availableTemplate) => (
                                        <option
                                            key={availableTemplate.id}
                                            value={availableTemplate.id}
                                        >
                                            {availableTemplate.name}
                                        </option>
                                    ))}
                                />
                                <button
                                    type="button"
                                    className="apply-template-button button button-bordered"
                                    disabled={isApplyTemplateButtonDisabled}
                                    onClick={applyTemplate}
                                >
                                    <FontAwesomeIcon icon="check" />&nbsp;{t('auctioneer-data-matrix.apply-template')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
                {items.length > 0 && (
                    <AuctioneerDataMatrixTable
                        items={items}
                        removeItem={removeItem}
                        changeQuantity={changeQuantity}
                        maxQuantity={maxQuantitySum}
                        isModifiable={isModifiable}
                        doReset={doReset}
                        doResetComponentsAfterModification={doResetComponentsAfterModification}
                        doAdjustValuesAfterTemplateApplied={adjustValuesAfterTemplateApplied}
                        doAdjustValuesAfterExternalUpdate={doAdjustValuesAfterExternalUpdate}
                    />
                )}
            </div>
            {isModifiable && isChanged && onReset && <ResetFieldButton onReset={onResetInternal} />}
        </div>
    );
}

export default AuctioneerDataMatrix;
